import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { createTextColumn } from '@ipfabric/utils';
import type { BgpCommunities } from '@ipfabric/utils/settings/types/types';
import classNames from 'classnames';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { type FC, memo, useMemo } from 'react';

const BareBGPCommunitiesList: FC<{
  bgps: BgpCommunities[];
  onDelete: (id: string) => void;
  onAddDeviceClick: () => void;
  onEditDevice: (bgp: BgpCommunities) => void;
  onImportExportClick: () => void;
}> = (props) => {
  const bgpCommunities = useMemo(
    () =>
      props.bgps.map((bgp) => {
        return {
          id: bgp.id,
          sn: bgp.sn,
          protocol: bgp.protocol,
          vrf: bgp.vrf,
          community: bgp.communityList.length,
          communityList: bgp.communityList,
        };
      }),
    [props.bgps],
  );

  const confirmDeleteDialog = useSimpleConfirmDialog();
  const requestBgpCommunityDelete = useStableCallback(
    async (row: BgpCommunities): Promise<boolean> => {
      if (
        await confirmDeleteDialog.show(getDeleteConfirmDialogMessages(row.sn))
      ) {
        props.onDelete(row.id);
        return true;
      }
      return false;
    },
  );

  const getDeleteConfirmDialogMessages = (name: string) => ({
    header: 'BGP community will be deleted',
    subHeader: `Do you really want to delete ${name}?`,
    btnConfirm: 'Delete',
    btnSecondaryConfirm: 'Cancel',
  });

  const editBgp = useStableCallback(props.onEditDevice);

  const columns = useMemo(
    () => [
      createTextColumn({
        key: 'id',
        title: 'ID',
        hidden: true,
      }),
      createTextColumn({
        key: 'sn',
        title: 'Serial number',
      }),
      createTextColumn({
        key: 'protocol',
        title: 'Protocol',
      }),
      createTextColumn({
        key: 'vrf',
        title: 'VRF',
      }),
      createTextColumn({
        key: 'community',
        title: '# of communities',
      }),
      createTextColumn({
        key: 'communityList',
        title: 'Community list',
        hidden: true,
      }),
      createFrontendOnlyColumn({
        key: 'actions',
        title: 'Actions',
        disabledSort: true,
        render: (row: BgpCommunities) => {
          return (
            <div className='flex flex-row items-center gap-xs'>
              <TableButton
                dataTestId={`bgp-communities-list-${row.sn}-edit-icon`}
                variant='plain'
                icon='fa-pen'
                size='small'
                onClick={() => {
                  const editedBgp = props.bgps.find((bgp) => bgp.id === row.id);
                  if (editedBgp === undefined) {
                    stumble('Internal error: edited bgp id not found.');
                    return;
                  }
                  editBgp(editedBgp);
                }}
                type='button'
              />
              <TableButton
                dataTestId={`bgp-communities-list-${row.sn}-remove-icon`}
                variant='plain'
                accent='negative'
                icon='fa-trash-can'
                size='small'
                onClick={() => requestBgpCommunityDelete(row)}
                type='button'
              />
            </div>
          );
        },
      }),
    ],
    [props.bgps],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: bgpCommunities,
  });

  return (
    <>
      <Table
        borderless
        rowHeight={44}
        description={
          'This list contains devices and communities from which routes are imported if limiting download of routes. ' +
          'If you are not downloading full routing tables from devices, and a ' +
          'row exists in the table below, routes will only be read from the ' +
          'mentioned device where they are tagged with the listed BGP community.'
        }
        className={classNames('flex-1', 'bg-transparent')}
        columns={columns}
        title='Device list'
        disableTableRowDetail
        menuItems={[
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='addDevice'
                variant='outline'
                icon='fa-plus-circle'
                onClick={() => {
                  props.onAddDeviceClick();
                }}
                type='button'
              >
                Add device
              </Button>
            ),
          },
          {
            standalone: true,
            elementWithKey: (
              <Button
                key='importExport'
                variant='outline'
                icon='fa-file-export'
                onClick={() => {
                  props.onImportExportClick();
                }}
                type='button'
              >
                Import/Export data
              </Button>
            ),
          },
        ]}
        {...frontendSimpleFilter}
      />
      {confirmDeleteDialog.render()}
    </>
  );
};

export const BgpCommunitiesList = memo(BareBGPCommunitiesList);
